import PersonUi from './PersonUi'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import Modal from './Modal'
import Cards from './Cards'
import Note from './Note'
import { NoteType } from '../enums/NoteType'
import LoadingSpinnerOverlay from './LoadingSpinnerOverlay'
import { LoadingCaption } from './LoadingSpinner'
import { useState, useMemo } from 'react'

interface SelectPersonModalProps {
    persons: PersonLiteDto[]
    title: string
    onClose: () => void
    onSelectChanged: (person: PersonLiteDto, selected: boolean) => void
    isLoading?: boolean
    captions?: LoadingCaption[]
    errMsg?: string
    multiSelectButtons?: React.ReactNode
}

function SelectPersonModal({
    persons,
    title,
    onClose,
    onSelectChanged,
    isLoading = false,
    captions,
    errMsg,
    multiSelectButtons,
}: SelectPersonModalProps): React.JSX.Element {
    const [searchTerm, setSearchTerm] = useState('')

    const sortedPersons = useMemo(() => {
        return [...persons].sort((a, b) => {
            const firstNameCompare = a.firstName.localeCompare(b.firstName)
            if (firstNameCompare !== 0) return firstNameCompare
            return a.lastName.localeCompare(b.lastName)
        })
    }, [persons])

    const filteredPersons = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return sortedPersons
        return sortedPersons.filter(
            (person) =>
                person.firstName.toLowerCase().includes(term) ||
                person.lastName.toLowerCase().includes(term),
        )
    }, [searchTerm, sortedPersons])

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [fadingOutIds, setFadingOutIds] = useState<Set<string>>(new Set())

    const handleSelect = (person: PersonLiteDto) => {
        const id = person.id
        if (selectedIds.has(id)) return

        setSelectedIds((prev) => new Set(prev).add(id))
        setFadingOutIds((prev) => new Set(prev).add(id))

        setTimeout(() => {
            onSelectChanged(person, true)
            setFadingOutIds((prev) => {
                const updated = new Set(prev)
                updated.delete(id)
                return updated
            })
        }, 1000)
    }

    const visiblePersons = filteredPersons.filter(
        (p) => !selectedIds.has(p.id) || fadingOutIds.has(p.id),
    )
    return (
        <Modal
            onClose={onClose}
            title={title}
            size="large"
            classes="h-[1000px] w-full"
        >
            <LoadingSpinnerOverlay show={isLoading} captions={captions}>
                <div className="flex flex-col items-start w-full">
                    {multiSelectButtons}
                    <div className="flex w-full mt-2">
                        <input
                            type="text"
                            className="form-input flex-grow"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {errMsg && <Note type={NoteType.ERROR}>{errMsg}</Note>}

                    {filteredPersons.length === 0 && !isLoading ? (
                        <Note type={NoteType.INFO} containerClassNames="mt-2">
                            No people found matching your search
                        </Note>
                    ) : (
                        <Cards
                            className={
                                multiSelectButtons
                                    ? 'mt-4 overflow-visible'
                                    : 'mt-2 overflow-visible'
                            }
                        >
                            {visiblePersons.map((person) => {
                                const isSelected = selectedIds.has(person.id)
                                const isFading = fadingOutIds.has(person.id)

                                return (
                                    <div
                                        key={person.id}
                                        className={`relative w-[300px] p-4 rounded-lg border transition-opacity duration-[2000ms] cursor-pointer truncate
                    ${
                        isSelected
                            ? 'bg-gray-200 border-gray-400'
                            : 'border-gray-300 hover:outline hover:outline-2 hover:outline-ctxt-blue'
                    }
                    ${
                        isFading
                            ? 'opacity-0 pointer-events-none'
                            : 'opacity-100'
                    }
                `}
                                        onClick={() => handleSelect(person)}
                                    >
                                        {/* Overlay */}
                                        {isSelected && (
                                            <div className="absolute inset-0 bg-white/80 z-30 flex items-center justify-center text-lg font-semibold text-green-700 pointer-events-none">
                                                Selected
                                            </div>
                                        )}

                                        <PersonUi person={person} />
                                    </div>
                                )
                            })}
                        </Cards>
                    )}
                </div>
            </LoadingSpinnerOverlay>
        </Modal>
    )
}

export default SelectPersonModal
